import { MutableRefObject, ReactNode } from "react";

import styles from "./DefaultMapSettings.module.scss";
import { StyledNumberInput } from "./shared";

export type MapCenterPickerValue = {
  latitude?: Nullable<number>;
  longitude?: Nullable<number>;
  zoom?: Nullable<number>;
};

type MapCenterPickerT = {
  containerId: string;
  containerRef?: MutableRefObject<HTMLDivElement | null>;
  value: MapCenterPickerValue;
  onLatitudeChange: (value: number) => void;
  onLongitudeChange: (value: number) => void;
  onZoomChange: (value: number) => void;
  enableZoom?: boolean;
  topExtra?: ReactNode;
  bottomActions?: ReactNode;
};

// Purely presentational — owns the map chrome (crosshair + floating inputs overlay) shared
// by the personal and organization map-defaults panels. It creates no Mapbox instance and
// does no data fetching; each caller keeps owning its own map lifecycle and mutations.
const MapCenterPicker = ({
  containerId,
  containerRef,
  value,
  onLatitudeChange,
  onLongitudeChange,
  onZoomChange,
  enableZoom = true,
  topExtra,
  bottomActions
}: MapCenterPickerT) => {
  return (
    <div className={styles.mapContainer} ref={containerRef}>
      <div id={containerId} className={styles.mapboxContainer} />

      <div className={styles.centerCrosshair}>
        <div className={styles.crosshairVertical} />
        <div className={styles.crosshairHorizontal} />
      </div>

      <div className={styles.floatingMapControls}>
        <div className={styles.topControls}>
          <div className={styles.floatingInputsRow}>
            <div className={styles.inputContainer}>
              <div className={styles.sectionLabel}>Latitude</div>
              <StyledNumberInput
                data-testid="input-latitude"
                aria-label="Latitude"
                step={0.000001}
                value={value?.latitude}
                onChange={onLatitudeChange}
              />
            </div>

            <div className={styles.inputContainer}>
              <div className={styles.sectionLabel}>Longitude</div>
              <StyledNumberInput
                data-testid="input-longitude"
                aria-label="Longitude"
                step={0.000001}
                value={value?.longitude}
                onChange={onLongitudeChange}
              />
            </div>

            {enableZoom && (
              <div className={styles.inputContainer}>
                <div className={styles.sectionLabel}>Zoom</div>
                <StyledNumberInput
                  data-testid="input-zoom"
                  aria-label="Zoom"
                  step={0.01}
                  value={value?.zoom}
                  onChange={onZoomChange}
                />
              </div>
            )}
          </div>

          {topExtra}
        </div>

        <div className={styles.bottomControls}>{bottomActions}</div>
      </div>
    </div>
  );
};

export default MapCenterPicker;
